import { flattenObject } from '../utils/flatten-object.mjs';
import { getPointOfSale } from '../utils/get-point-of-sale.mjs';

// © Sitecore Corporation A/S. All rights reserved. Sitecore® is a registered trademark of Sitecore Corporation A/S.
class Personalizer {
    /**
     * The Personalizer Class runs a flow of interactive experiments.
     * @param personalizeClient - The data to be send to Sitecore CDP
     * @param infer - The source of methods to estimate language and page parameters
     */
    constructor(personalizeClient, id, infer) {
        this.personalizeClient = personalizeClient;
        this.id = id;
        this.infer = infer;
    }
    /**
     * A function to make a request to the Sitecore CDP /callFlows API endpoint
     * @param timeout - Optional timeout in milliseconds to cancel the request
     * @returns - A promise that resolves with either the Sitecore CDP response object or null
     */
    async getInteractiveExperienceData(personalizeInput, timeout) {
        this.validate(personalizeInput);
        const sanitizedInput = this.sanitizeInput(personalizeInput);
        const mappedData = this.mapPersonalizeInputToCDPData(sanitizedInput);
        if (!mappedData.email && !mappedData.identifiers)
            mappedData.browserId = this.id;
        const response = await this.personalizeClient.sendCallFlowsRequest(mappedData, timeout);
        return response;
    }
    /**
     * A function that sanitizes the personalize input data
     * @returns - The sanitized object
     */
    sanitizeInput(personalizerInput) {
        const sanitizedInput = {
            channel: personalizerInput.channel,
            currency: personalizerInput.currency,
            friendlyId: personalizerInput.friendlyId,
            language: personalizerInput.language,
            pointOfSale: getPointOfSale(personalizerInput.pointOfSale || this.personalizeClient.settings.pointOfSale),
        };
        if (personalizerInput.identifier &&
            personalizerInput.identifier.id &&
            personalizerInput.identifier.id.trim().length > 0)
            sanitizedInput.identifier = personalizerInput.identifier;
        if (personalizerInput.email && personalizerInput.email.trim().length > 0)
            sanitizedInput.email = personalizerInput.email;
        if (personalizerInput.params && Object.keys(personalizerInput.params).length > 0)
            sanitizedInput.params = flattenObject({ object: personalizerInput.params });
        return sanitizedInput;
    }
    /**
     * A function that maps the personalize input data with the CDP
     * @returns - The CDP object
     */
    mapPersonalizeInputToCDPData(input) {
        var _a, _b;
        const mappedData = {
            channel: input.channel,
            clientKey: this.personalizeClient.settings.clientKey,
            currencyCode: input.currency,
            email: input.email,
            friendlyId: input.friendlyId,
            identifiers: input.identifier,
            language: (_a = input.language) !== null && _a !== void 0 ? _a : (_b = this.infer) === null || _b === void 0 ? void 0 : _b.language(),
            params: input.params,
            pointOfSale: input.pointOfSale,
        };
        return mappedData;
    }
    /**
     * A validation method to throw error for the mandatory property for runtime users
     */
    validate({ friendlyId }) {
        if (!friendlyId || friendlyId.trim().length === 0)
            throw new Error(`[MV-0008] "friendlyId" is required.`);
    }
}

export { Personalizer };
